/*
 * PREFERENCE STORE SAGAS
 *
 * These routines listen for request actions, and satisfies them.  These are asynchronous routines, and upon
 * completion will dispatch a new action with the results
 *
 *
 */

import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';

import { getEnvironmentConfig } from '../../utils/core';
import { resourceSagaUtils, resourceStoreTypes } from '../core';

import * as actions from './actions';

// ===============================================================================================
// SAGAS functions for preferences.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.  Data is returned via ACTIONS, however, these can be
// split out (and should be) to be fully functional and JS shareable API callers via Axios and
// simply return an object in a provided callback, which can then choose to call the actions
//

function constructPreferencesUrl(data) {
  let requestUrl;
  let urlSection;
  switch (data?.section) {
    case 'shared':
      urlSection = data?.group === 'dataset' ? '' : `/${data?.group}`;
      requestUrl = `${getEnvironmentConfig().services_url}/preferences${urlSection}`;
      break;

    case 'client':
      urlSection = data?.group === 'dataset' ? '' : `/${data?.group}`;
      requestUrl = `${getEnvironmentConfig().services_url}/preferences/client${urlSection}`;
      break;

    default:
      requestUrl = `${getEnvironmentConfig().services_url}/preferences`;
      break;
  }

  return requestUrl;
}

// ====================================================
// GET PREFERENCES
//
// specify the section and they group as follows { section: <string>, group: <string> }
//
// SECTION: one of 'shared', 'client', 'qcs'
// GROUP: shared, one of 'user', 'dataset;
//        client, one of 'user', 'dataset', 'global'
//        qcs: only top level
//
export function* getPreferences(action) {
  try {
    const requestUrl = constructPreferencesUrl(action.payload);

    // SUCCESS CALLBACK
    //
    // convert the preference array to an immutable object
    // you can do any transformation here to convert from 3rd party format
    // to your internal state format
    // remove deleted
    const config = resourceStoreTypes.mkQcsSyncResourceConfig({
      resourceName: 'preferences',
      resourceBaseUrl: requestUrl,
      successAction: actions.getPreferencesSuccess,
      failureAction: actions.getPreferencesFailure,
      transformResourceToRequestData: (resource) => resource.preference,
      transformResponseToResource: (response) => ({ ...response, path: action.payload }),
      ...(action.payload &&
        action.payload.group !== 'dataset' && { axiosConfig: { headers: { 'qcs-dataset-id': null } } }),
    });
    yield call(resourceSagaUtils.qcsSyncGetResource, config, action);
  } catch (error) {
    // ERROR CALLBACK
    // todo: make sure this is an axios error
    assert(false, error);

    yield put(actions.getPreferencesFailure(error));
  }
}

// ====================================================
// SET PREFERENCE
// if you want to use yield, put a star here
//
// Pass in an object { section, group, preference }
//
// SECTIONS: one of 'shared', 'client', 'qcs'
// GROUP: shared, one of 'user', 'dataset;
//        client, one of 'user', 'dataset', 'global'
//        qcs: only top level
//
//
// preference is a object structure following the rules described here (do not send JSON, we convert for you)
// https://quickenteam.atlassian.net/wiki/spaces/EN/pages/99037174/Preference+Service
//
//

export function* setPreference(action) {
  const config = resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'preferences',
    resourceBaseUrl: action.payload?.group !== 'dataset' ? `/preferences/${action.payload?.group}` : '/preferences',
    successAction: actions.setPreferenceSuccess,
    failureAction: actions.setPreferenceFailure,
    transformResourceToRequestData: (resource) => resource.preference,
    ...(action.payload?.group !== 'dataset' && { axiosConfig: { headers: { 'qcs-dataset-id': null } } }),
  });
  yield call(resourceSagaUtils.qcsSyncUpdateResource, config, action);
}

// ====================================================
// DELETE PREFERENCE
// if you want to use yield, put a star here
export function* deletePreference({ payload: _data }) {
  /*
    let requestUrl = `${getEnvironmentConfig().services_url}/preferences`;
    requestUrl += `/${data.id}`;
    log.log(`In SAGAS: Delete Preference ${requestUrl}`, data);

    try {
      const qcsAxios = AxiosFactory.get('qcs');

      // yield below causes the call to happen async, code following it is the
      // callback functions (one for success, one for error)
      //
      // assumes that the data passed in is valid and per the QCS spec
      yield call(qcsAxios.delete, requestUrl, data);

      // create an immutable object
      // const preference = types.Preference(data);
      yield put(actions.deletePreferenceSuccess(data.id));
    } catch (error) {
      log.log('Update Preference Error...', error);
      yield put(actions.deletePreferenceFailure(error));
    }
  */
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* getPreferencesActionWatcher() {
  yield takeEvery(actions.getPreferences, getPreferences);
}

export function* getPreferencesUserActionWatcher() {
  yield takeLeading(actions.getPreferencesUser, function* getPreferencesUser(action) {
    yield call(getPreferences, {
      ...action,
      payload: {
        section: 'shared',
        group: 'user',
        ...action.payload,
      },
    });
  });
}

export function* getPreferencesDatasetActionWatcher() {
  yield takeLeading(actions.getPreferencesDataset, function* getPreferencesDataset(action) {
    yield call(getPreferences, {
      ...action,
      payload: {
        section: 'shared',
        group: 'dataset',
        ...action.payload,
      },
    });
  });
}

export function* setPreferenceActionWatcher() {
  yield takeEvery(actions.setPreference, setPreference);
}

export function* deletePreferenceActionWatcher() {
  yield takeEvery(actions.deletePreference, deletePreference);
}

// ====================================================
// EXPORTS

export default [
  getPreferencesActionWatcher,
  getPreferencesDatasetActionWatcher,
  getPreferencesUserActionWatcher,
  setPreferenceActionWatcher,
  deletePreferenceActionWatcher,
];
